import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Trash2 } from "lucide-react";
import { api, errText, CURRENCIES, FREQ_LABEL } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DictSelect from "@/components/DictSelect";

const today = () => new Date().toISOString().slice(0, 10);

const empty = (companyId) => ({
  type: "income",
  company_id: companyId || "",
  target_company_id: "",
  project_id: "",
  category_id: "",
  name: "",
  counterparty_type: "custom",
  counterparty_id: "",
  counterparty_name: "",
  amount: "",
  currency: "USD",
  tax: 0,
  date: today(),
  frequency: "once",
  period_days: 30,
  links: [],
  comment: "",
});

export default function OperationDialog({ open, onOpenChange, dicts, reloadDicts, operation, defaultCompany, onSaved }) {
  const [form, setForm] = useState(empty(defaultCompany));
  const [linkInput, setLinkInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(operation ? { ...empty(defaultCompany), ...operation, tax: operation.tax || 0 } : empty(defaultCompany));
    setLinkInput("");
  }, [open, operation, defaultCompany]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.company_id || !form.name || !form.amount) {
      toast.error("Заполните компанию, название и сумму");
      return;
    }
    if (form.type === "transfer" && !form.target_company_id) {
      toast.error("Укажите компанию-получателя перевода");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      amount: Number(form.amount),
      tax: Number(form.tax || 0),
      period_days: form.frequency === "custom" ? Number(form.period_days || 30) : null,
      target_company_id: form.type === "transfer" ? form.target_company_id : null,
      project_id: form.project_id || null,
      category_id: form.category_id || null,
      counterparty_id: form.counterparty_type === "custom" ? null : form.counterparty_id || null,
      counterparty_name:
        form.counterparty_type === "custom"
          ? form.counterparty_name || null
          : form.counterparty_type === "company"
          ? dicts.companies.find((c) => c.id === form.counterparty_id)?.name || null
          : dicts.counterparties.find((c) => c.id === form.counterparty_id)?.name || null,
    };
    try {
      if (operation?.id) await api.put(`/operations/${operation.id}`, payload);
      else await api.post("/operations", payload);
      toast.success(operation?.id ? "Операция обновлена" : "Операция создана");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="operation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {operation?.id ? "Редактировать операцию" : "Новая операция"}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={form.type} onValueChange={(v) => set("type", v)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="income" data-testid="op-type-income">Доход</TabsTrigger>
            <TabsTrigger value="transfer" data-testid="op-type-transfer">Перевод</TabsTrigger>
            <TabsTrigger value="expense" data-testid="op-type-expense">Расход</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Компания {form.type === "transfer" ? "(отправитель)" : ""}</Label>
            <DictSelect
              kind="companies"
              testid="op-company"
              items={dicts.companies}
              value={form.company_id}
              onChange={(v) => set("company_id", v)}
              onCreated={reloadDicts}
              placeholder="Выберите компанию"
            />
          </div>

          {form.type === "transfer" && (
            <div className="space-y-2">
              <Label>Компания-получатель</Label>
              <DictSelect
                kind="companies"
                testid="op-target-company"
                items={dicts.companies.filter((c) => c.id !== form.company_id)}
                value={form.target_company_id}
                onChange={(v) => set("target_company_id", v)}
                onCreated={reloadDicts}
                placeholder="Куда переводим"
              />
            </div>
          )}

          <div className="space-y-2 sm:col-span-2">
            <Label>Название</Label>
            <Input
              value={form.name}
              data-testid="op-name"
              placeholder="Например: Оплата по договору №12"
              onChange={(e) => set("name", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Проект</Label>
            <DictSelect
              kind="projects"
              testid="op-project"
              items={dicts.projects}
              value={form.project_id}
              onChange={(v) => set("project_id", v)}
              onCreated={reloadDicts}
              placeholder="Проект"
            />
          </div>

          <div className="space-y-2">
            <Label>Категория</Label>
            <DictSelect
              kind="categories"
              testid="op-category"
              items={dicts.categories}
              value={form.category_id}
              onChange={(v) => set("category_id", v)}
              onCreated={reloadDicts}
              placeholder="Категория"
            />
          </div>

          <div className="space-y-2">
            <Label>Тип контрагента</Label>
            <Select value={form.counterparty_type} onValueChange={(v) => set("counterparty_type", v)}>
              <SelectTrigger data-testid="op-counterparty-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="company">Наша компания</SelectItem>
                <SelectItem value="counterparty">Из справочника</SelectItem>
                <SelectItem value="custom">Вручную</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Контрагент</Label>
            {form.counterparty_type === "custom" ? (
              <Input
                value={form.counterparty_name || ""}
                data-testid="op-counterparty-custom"
                placeholder="Название контрагента"
                onChange={(e) => set("counterparty_name", e.target.value)}
              />
            ) : (
              <DictSelect
                kind={form.counterparty_type === "company" ? "companies" : "counterparties"}
                testid="op-counterparty"
                items={form.counterparty_type === "company" ? dicts.companies : dicts.counterparties}
                value={form.counterparty_id}
                onChange={(v) => set("counterparty_id", v)}
                onCreated={reloadDicts}
                placeholder="Контрагент"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>Сумма</Label>
            <Input
              type="number"
              value={form.amount}
              data-testid="op-amount"
              onChange={(e) => set("amount", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Валюта</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="op-currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Налог, %</Label>
            <Input type="number" value={form.tax} data-testid="op-tax" onChange={(e) => set("tax", e.target.value)} />
            <p className="text-[11px] text-muted-foreground">
              {form.type === "transfer"
                ? "Налог добавляется к списанию у компании-отправителя; получателю зачисляется сумма без налога."
                : "Для доходов и расходов налог уже включён в сумму (справочно)."}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Дата операции / начала</Label>
            <Input type="date" value={form.date} data-testid="op-date" onChange={(e) => set("date", e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>Частота</Label>
            <Select value={form.frequency} onValueChange={(v) => set("frequency", v)} disabled={!!operation?.id}>
              <SelectTrigger data-testid="op-frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(FREQ_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.frequency === "custom" && (
            <div className="space-y-2">
              <Label>Период, дней</Label>
              <Input
                type="number"
                value={form.period_days}
                data-testid="op-period-days"
                onChange={(e) => set("period_days", e.target.value)}
              />
            </div>
          )}

          <div className="space-y-2 sm:col-span-2">
            <Label>Ссылки (документы, счета)</Label>
            <div className="flex gap-2">
              <Input
                value={linkInput}
                placeholder="https://…"
                data-testid="op-link-input"
                onChange={(e) => setLinkInput(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                data-testid="op-link-add"
                onClick={() => {
                  if (!linkInput.trim()) return;
                  set("links", [...(form.links || []), linkInput.trim()]);
                  setLinkInput("");
                }}
              >
                <Link2 className="h-4 w-4" />
              </Button>
            </div>
            {(form.links || []).map((l, i) => (
              <div key={`${l}-${i}`} className="flex items-center justify-between rounded-md bg-muted px-3 py-1.5 text-xs">
                <span className="truncate">{l}</span>
                <button
                  type="button"
                  data-testid={`op-link-remove-${i}`}
                  onClick={() => set("links", form.links.filter((_, idx) => idx !== i))}
                >
                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                </button>
              </div>
            ))}
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label>Комментарий</Label>
            <Textarea
              value={form.comment || ""}
              data-testid="op-comment"
              rows={2}
              onChange={(e) => set("comment", e.target.value)}
            />
          </div>
        </div>

        {form.date > today() && (
          <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning" data-testid="op-future-note">
            Дата в будущем — операция будет создана как «Запланировано» и потребует подтверждения администратором.
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="op-cancel">Отмена</Button>
          <Button onClick={save} disabled={saving} data-testid="op-save">
            {saving ? "Сохранение…" : "Сохранить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
